import { Box, Divider, Typography } from '@mui/material';
import { compact } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControls,
  FormikCheckbox,
  FormikNumericField,
  FormikTextField,
  PaymentTypeAmountLimits,
  TradeMethodTreeSelect,
} from 'components';
import { P2PProviderType } from 'enums';
import { useCurrencies, useUserContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { TradeMethod } from 'types';

import { ConfigOverridesField } from '../ConfigOverridesField';
import { P2PProviderCredentials } from '../P2PProviderCredentials';

type Props = {
  providerType?: P2PProviderType;
  fiatCurrencyId?: string;
  assetCurrencyId?: string;
  onTradeMethodsChange: (event: any) => void;
  isNew?: boolean;
};

export const P2PProviderPayoutConfig: React.FC<Props> = ({
  providerType,
  fiatCurrencyId,
  assetCurrencyId,
  isNew,
}) => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.p2p_providers',
  });

  const { getFiatCurrencyCode } = useCurrencies();

  const { tradeMethods, allTradeMethods } = useUserContext();

  const availableTradeMethods: TradeMethod[] = useMemo(
    () => (isNew ? tradeMethods : allTradeMethods),
    [tradeMethods, allTradeMethods, isNew],
  );

  const fiatCurrenciesIds = useMemo(
    () => compact([fiatCurrencyId]),
    [fiatCurrencyId],
  );

  return (
    <Fragment>
      <Typography variant="h5">{tCommon('common.payout')}</Typography>
      <FormikCheckbox label={t('fields.enabled')} name="payoutConfig.enabled" />
      <FormikCheckbox
        label={t('fields.polling_enabled')}
        name="payoutConfig.pollingEnabled"
      />
      <FormikTextField
        label={t('fields.url')}
        name="payoutConfig.url"
        required
      />

      <P2PProviderCredentials
        path="payoutConfig.credentials"
        providerType={providerType}
      />

      <FormikNumericField
        label={t('fields.response_timeout')}
        name="payoutConfig.responseTimeout"
        suffix={tCommon('suffixes.ms')}
        allowNegative={false}
        defaultValue={0}
        fullWidth
        helperText={t('fields.response_timeout_description')}
      />

      <FormikNumericField
        label={t('fields.order_timeout')}
        name="payoutConfig.orderTimeout"
        suffix={tCommon('suffixes.minutes')}
        allowNegative={false}
        required
        defaultValue={0}
        fullWidth
        helperText={t('fields.order_timeout_description')}
      />

      <FormikNumericField
        label={t('fields.expected_fee')}
        name="payoutConfig.expectedFee"
        allowNegative={false}
        percentageSuffix
        fullWidth
        required
      />

      <Fragment>
        <Typography>
          {`${t('fields.trade_methods')} (${tCommon('common.payout')})`}
        </Typography>

        <TradeMethodTreeSelect
          fieldName={'payoutConfig.tradeMethods'}
          tradeMethods={availableTradeMethods}
          fiatCurrenciesIds={fiatCurrenciesIds}
        />
      </Fragment>

      <Box sx={{ mt: 2, mb: 3 }}>
        <PaymentTypeAmountLimits
          configPath="payoutConfig"
          fiatCurrencyCode={getFiatCurrencyCode(fiatCurrencyId)}
        />
      </Box>

      <ConfigOverridesField
        fieldPath="payoutConfig"
        providerType={providerType}
      />

      <Divider sx={{ mt: 2, mb: 2 }}>{t('fields.order_amount_limits')}</Divider>
      <FormControls row>
        <FormikNumericField
          name="payoutConfig.orderMinAmount"
          label={tCommon('common.min')}
          fullWidth
          suffix={getFiatCurrencyCode(fiatCurrencyId)}
          allowNegative={false}
        />
        <FormikNumericField
          name="payoutConfig.orderMaxAmount"
          label={tCommon('common.max')}
          fullWidth
          suffix={getFiatCurrencyCode(fiatCurrencyId)}
          allowNegative={false}
        />
      </FormControls>
    </Fragment>
  );
};
